/** W076.1 Today hero: the morning brief that reached the user's
 *  device, with a "Delivered at HH:MM" proof line and the three
 *  curated actionable items underneath.
 *
 *  Renders:
 *    - Headline as the lede.
 *    - A delivery proof badge: green "Delivered at HH:MM" when
 *      briefs.delivered_at is non-null, otherwise an amber "Not
 *      delivered yet, enable notifications above" nudge that
 *      cross-references the PushEnableBanner so the user sees the
 *      cause and the fix in the same field of view.
 *    - At most three actionable brief_items pulled via the existing
 *      /api/brief/items endpoint. Each item shows title + due-by chip;
 *      tapping the row drills into /attention with the item focused
 *      (the unified attention surface lands in W076.4; the link works
 *      today as /reconcile fallback).
 *
 *  Quiet by design: if there is no brief yet (first run, fresh tenant)
 *  the component returns null so the Today layout collapses cleanly.
 *
 *  Brand voice: lexicon ok, no em dashes, "Memso" never "brain". */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  CircleAlert,
  Clock,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { api } from "../lib/api";
import type { BriefPayload } from "../anchors/settings/connections/api";

interface BriefItem {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  status: string;
  priority: number;
  due_at: string | null;
}

interface BriefItemsResponse {
  items: BriefItem[];
}

function formatDeliveredAt(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch {
    return null;
  }
}

function formatDueChip(iso: string | null): string | null {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const now = new Date();
    const ms = d.getTime() - now.getTime();
    const days = Math.round(ms / (24 * 60 * 60 * 1000));
    if (days < 0) return `${Math.abs(days)}d overdue`;
    if (days === 0) return "due today";
    if (days === 1) return "due tomorrow";
    if (days <= 7) return `due in ${days}d`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return null;
  }
}

export function DeliveredBriefHero() {
  const [brief, setBrief] = useState<BriefPayload | null>(null);
  const [items, setItems] = useState<BriefItem[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [briefRes, itemsRes] = await Promise.all([
          api.briefLatest(),
          fetch(`/api/brief/items?status=open&limit=3`, { credentials: "same-origin" })
            .then((r) => (r.ok ? (r.json() as Promise<BriefItemsResponse>) : { items: [] }))
            .catch(() => ({ items: [] as BriefItem[] })),
        ]);
        if (cancelled) return;
        setBrief(briefRes.brief ?? null);
        setItems(Array.isArray(itemsRes.items) ? itemsRes.items.slice(0, 3) : []);
      } catch {
        /* hero is best-effort; the rest of Today still renders */
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!loaded) return null;
  if (!brief || !brief.headline) return null;

  const deliveredAt = formatDeliveredAt(brief.delivered_at ?? null);
  const isDelivered = !!deliveredAt;
  const generatedDate = (() => {
    try {
      return new Date(brief.generated_at).toLocaleDateString(undefined, {
        weekday: "long",
        month: "short",
        day: "numeric",
      });
    } catch {
      return brief.generated_at.slice(0, 10);
    }
  })();

  return (
    <section
      data-testid="delivered-brief-hero"
      className="rounded-2xl border border-indigo-200 dark:border-indigo-900 bg-gradient-to-br from-indigo-50 via-white to-sky-50 dark:from-indigo-950/40 dark:via-slate-900/60 dark:to-sky-950/30 p-5 sm:p-6 space-y-4"
    >
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <span className="inline-flex w-9 h-9 rounded-xl items-center justify-center bg-gradient-to-br from-indigo-500 to-sky-500 text-white shadow-sm flex-shrink-0">
            <Sparkles className="w-4.5 h-4.5" />
          </span>
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
              Morning brief, {generatedDate}
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100 leading-snug">
              {brief.headline}
            </h2>
          </div>
        </div>
        {isDelivered ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 text-xs font-semibold px-3 py-1.5 whitespace-nowrap">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Delivered at {deliveredAt}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 text-xs font-semibold px-3 py-1.5 whitespace-nowrap">
            <CircleAlert className="w-3.5 h-3.5" />
            Not delivered yet
          </span>
        )}
      </header>

      {items.length > 0 && (
        <ul className="space-y-2">
          {items.map((it) => {
            const dueChip = formatDueChip(it.due_at);
            return (
              <li key={it.id}>
                <Link
                  to="/reconcile"
                  className="group flex items-center gap-3 rounded-xl bg-white/80 dark:bg-slate-800/60 hover:bg-white dark:hover:bg-slate-800 border border-slate-200/80 dark:border-slate-700/60 px-3 py-2.5 transition-colors"
                >
                  <span className="inline-flex w-7 h-7 rounded-lg items-center justify-center bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 text-xs font-bold flex-shrink-0">
                    {String(items.indexOf(it) + 1)}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
                      {it.title}
                    </span>
                    {dueChip && (
                      <span className="mt-0.5 inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                        <Clock className="w-3 h-3" />
                        {dueChip}
                      </span>
                    )}
                  </span>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200 transition-colors flex-shrink-0" />
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {items.length === 0 && brief.body_md && (
        <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-line line-clamp-4">
          {brief.body_md.replace(/[#*_`]/g, "").trim()}
        </p>
      )}
    </section>
  );
}
